import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const monoBoxSx = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "16px",
  color: "#142C5C",
  backgroundColor: "#f3f5f9",
  border: "1px solid #d9dee8",
  borderRadius: "6px",
  px: 1.5,
  py: 1,
  flex: 1,
  wordBreak: "break-all",
};

/**
 * Shown once after creating an account or resetting its password. The
 * password is not stored anywhere readable, so this is the only chance to
 * copy it.
 */
const TempPasswordDialog = ({ open, onClose, data }) => {
  const [copied, setCopied] = useState("");

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      // Clipboard unavailable (insecure context); the user can select the text.
    }
  };

  const username = data?.account?.username || "";
  const password = data?.temporaryPassword || "";
  const loginUrl = typeof window !== "undefined" ? `${window.location.origin}/` : "/";
  const everything = `Admin panel: ${loginUrl}\nUsername: ${username}\nTemporary password: ${password}`;

  const Row = ({ label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ width: 150, color: "#555", fontSize: 14 }}>{label}</Typography>
      <Box sx={monoBoxSx}>{value}</Box>
      <Tooltip title={copied === label ? "Copied" : "Copy"}>
        <IconButton size="small" onClick={() => copy(label, value)}>
          {copied === label ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#142C5C", fontWeight: 700 }}>
        {data?.mode === "reset" ? "Password reset" : "Account created"}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This temporary password is shown only once. Copy it now and share it with the
          person securely. They will be asked to set their own password on first sign-in.
        </Alert>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Row label="Username" value={username} />
          <Row label="Temporary password" value={password} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          startIcon={copied === "all" ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={() => copy("all", everything)}
        >
          {copied === "all" ? "Copied" : "Copy sign-in details"}
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ background: "#142C5C", "&:hover": { background: "#142C5C" } }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TempPasswordDialog;
